/*
 * Broadcast Theme
 *
 * Use this file to add custom Javascript to Broadcast. Keeping your custom
 * Javascript in this file will make it easier to update Broadcast. In order
 * to use this file you will need to open layout/theme.liquid and uncomment
 * the custom.js script import line near the bottom of the file.
 */

(function () {
  // --------------------------------------------------
  // Helpers
  // --------------------------------------------------
  const onReady = (cb) => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", cb, { once: true });
    } else {
      cb();
    }
  };

  const runDelayed = (fn, delays = [50, 250, 800]) => {
    fn();
    delays.forEach((delay) => setTimeout(fn, delay));
  };

  // --------------------------------------------------
  // Modal
  // --------------------------------------------------
  class Modal {
    constructor() {
      this.root = document.querySelector("#beerlivery");
      if (!this.root) return;

      this.ctaBlock = this.root.querySelector(".product__block--buttons");
      this.appBlock = this.root.querySelector(".shopify-app-block");
      this.yesRadio = document.querySelector(".location-button-wrapper #yes");
      this.noRadio = document.querySelector(".location-button-wrapper #no");
      this.inputs = document.querySelectorAll(".location-button-wrapper input");

      if (
        !this.ctaBlock ||
        !this.appBlock ||
        !this.yesRadio ||
        !this.inputs.length
      ) {
        return;
      }

      this._init();
    }

    _displayChange() {
      const displayStyle = this.yesRadio.checked ? "block" : "none";
      this.ctaBlock.style.display = displayStyle;
      this.appBlock.style.display = displayStyle;
    }

    _init() {
      this.inputs.forEach((input) => {
        input.addEventListener("change", () => this._displayChange(), {
          passive: true,
        });
      });

      this._displayChange();
    }
  }

  // --------------------------------------------------
  // External link handler (delegated)
  // --------------------------------------------------
  const externalLinkHandler = {
    _bound: false,

    init() {
      if (this._bound) return;
      this._bound = true;

      document.addEventListener("click", (event) => {
        const link = event.target.closest("a[href]");
        if (!link) return;

        const href = link.getAttribute("href");
        if (
          !href ||
          href.startsWith("#") ||
          href.startsWith("javascript:") ||
          href.startsWith("mailto:") ||
          href.startsWith("tel:")
        ) {
          return;
        }

        if (link.target) return;

        try {
          const url = new URL(link.href, window.location.origin);
          if (url.hostname !== window.location.hostname) {
            link.target = "_blank";
            link.rel = "noreferrer noopener";
          }
        } catch (e) {
          // ignore malformed URLs
        }
      });
    },
  };

  // --------------------------------------------------
  // Horizontal scrollbar
  // --------------------------------------------------
  const HScroll = {
    init() {
      const roots = document.querySelectorAll("[data-hscroll]");
      if (!roots.length) return;
      roots.forEach((root) => this._initOne(root));
    },

    _initOne(root) {
      if (root.dataset.hscrollInitialized === "true") return;
      root.dataset.hscrollInitialized = "true";

      const scroller = root.querySelector("[data-hscroll-scroller]");
      const bar = root.querySelector("[data-hscroll-bar]");
      const thumb = root.querySelector("[data-hscroll-thumb]");
      if (!scroller || !bar || !thumb) return;

      let rafId = null;

      const sync = () => {
        rafId = null;

        const maxScroll = scroller.scrollWidth - scroller.clientWidth;
        const barW = bar.clientWidth;

        const thumbW = Math.max(
          44,
          (scroller.clientWidth / scroller.scrollWidth) * barW
        );
        const maxThumb = Math.max(0, barW - thumbW);

        thumb.style.width = `${thumbW}px`;

        const left = maxScroll
          ? (scroller.scrollLeft / maxScroll) * maxThumb
          : 0;

        thumb.style.transform = `translateX(${left}px)`;
      };

      const syncQueued = () => {
        if (rafId !== null) return;
        rafId = requestAnimationFrame(sync);
      };

      bar.addEventListener("pointerdown", (e) => {
        if (e.target === thumb) return;

        const rect = bar.getBoundingClientRect();
        const x = e.clientX - rect.left;

        const barW = bar.clientWidth;
        const thumbW = thumb.offsetWidth;
        const maxThumb = Math.max(0, barW - thumbW);

        const nextLeft = Math.min(maxThumb, Math.max(0, x - thumbW / 2));
        const ratio = maxThumb ? nextLeft / maxThumb : 0;

        scroller.scrollLeft =
          ratio * (scroller.scrollWidth - scroller.clientWidth);
      });

      let dragging = false;
      let startX = 0;
      let startLeft = 0;

      const getCurrentLeft = () => {
        const tr = getComputedStyle(thumb).transform;
        if (!tr || tr === "none") return 0;

        const match = tr.match(/matrix\(([^)]+)\)/);
        if (!match) return 0;

        const parts = match[1].split(",").map((p) => parseFloat(p.trim()));
        return Number.isFinite(parts[4]) ? parts[4] : 0;
      };

      thumb.addEventListener("pointerdown", (e) => {
        dragging = true;
        thumb.setPointerCapture(e.pointerId);
        startX = e.clientX;
        startLeft = getCurrentLeft();
        e.preventDefault();
      });

      thumb.addEventListener("pointermove", (e) => {
        if (!dragging) return;

        const barW = bar.clientWidth;
        const thumbW = thumb.offsetWidth;
        const maxThumb = Math.max(0, barW - thumbW);

        const nextLeft = Math.min(
          maxThumb,
          Math.max(0, startLeft + (e.clientX - startX))
        );

        const ratio = maxThumb ? nextLeft / maxThumb : 0;
        scroller.scrollLeft =
          ratio * (scroller.scrollWidth - scroller.clientWidth);
      });

      const stopDrag = () => {
        dragging = false;
      };

      thumb.addEventListener("pointerup", stopDrag);
      thumb.addEventListener("pointercancel", stopDrag);

      scroller.addEventListener("scroll", syncQueued, { passive: true });
      window.addEventListener("resize", syncQueued, { passive: true });

      runDelayed(syncQueued);
    },
  };

  // --------------------------------------------------
  // Accordion default-open fix
  // --------------------------------------------------
  const AccordionDefaultOpenFix = {
    init() {
      const sections = document.querySelectorAll(".custom-accordion-group");
      if (!sections.length) return;

      const applyAll = () => sections.forEach((section) => this.apply(section));
      runDelayed(applyAll);
    },

    apply(section) {
      const container = section.querySelector("collapsible-elements");
      const isSingle =
        container && container.getAttribute("single") === "true";

      const defaults = section.querySelectorAll(
        'details.accordion[data-default-open="true"]'
      );
      if (!defaults.length) return;

      const toOpen = isSingle ? [defaults[0]] : [...defaults];

      if (isSingle) {
        section
          .querySelectorAll(
            "details.accordion[open], details.accordion[open='true']"
          )
          .forEach((details) => {
            if (!toOpen.includes(details)) {
              details.open = false;
              details.removeAttribute("open");
            }
          });
      }

      toOpen.forEach((details) => {
        details.open = true;
        details.setAttribute("open", "true");

        const body = details.querySelector("[data-collapsible-body]");
        if (body) body.style.height = "auto";
      });
    },
  };

  // --------------------------------------------------
  // Blog share
  // --------------------------------------------------
  const BrickShare = {
    init() {
      const wraps = document.querySelectorAll("[data-brick-share]");
      if (!wraps.length) return;

      wraps.forEach((wrap) => {
        if (wrap.dataset.brickShareInitialized === "true") return;
        wrap.dataset.brickShareInitialized = "true";

        const toast = wrap.querySelector("[data-share-toast]");
        const btns = wrap.querySelectorAll("[data-share]");
        if (!btns.length) return;

        const pageUrl = window.location.href;
        const pageTitle = document.title;

        const showToast = (msg) => {
          if (!toast) return;
          toast.textContent = msg;
          clearTimeout(toast._t);
          toast._t = setTimeout(() => {
            toast.textContent = "";
          }, 1800);
        };

        const copy = async () => {
          try {
            await navigator.clipboard.writeText(pageUrl);
            showToast("Link copied");
          } catch (e) {
            const ta = document.createElement("textarea");
            ta.value = pageUrl;
            ta.setAttribute("readonly", "");
            ta.style.position = "absolute";
            ta.style.left = "-9999px";
            document.body.appendChild(ta);
            ta.select();
            document.execCommand("copy");
            document.body.removeChild(ta);
            showToast("Link copied");
          }
        };

        const openPopup = (url) => {
          window.open(
            url,
            "_blank",
            "noopener,noreferrer,width=600,height=600"
          );
        };

        btns.forEach((el) => {
          el.addEventListener("click", async (e) => {
            e.preventDefault();
            const type = el.getAttribute("data-share");

            if (type === "copy") {
              await copy();
              return;
            }

            if (type === "twitter") {
              const shareUrl =
                "https://twitter.com/intent/tweet?url=" +
                encodeURIComponent(pageUrl) +
                "&text=" +
                encodeURIComponent(pageTitle);
              openPopup(shareUrl);
              return;
            }

            if (type === "linkedin") {
              const shareUrl =
                "https://www.linkedin.com/sharing/share-offsite/?url=" +
                encodeURIComponent(pageUrl);
              openPopup(shareUrl);
              return;
            }

            if (type === "instagram") {
              await copy();
              const igApp = "instagram://app";
              const igWeb = "https://www.instagram.com/";
              window.location.href = igApp;
              setTimeout(() => {
                window.open(igWeb, "_blank", "noopener,noreferrer");
              }, 500);
            }
          });
        });
      });
    },
  };

  // --------------------------------------------------
  // Init on ready
  // --------------------------------------------------
  onReady(() => {
    // GSAP / ScrollTrigger / Draggable / Flip
    // Keeping the GSAP logic unchanged
    if (
      typeof gsap !== "undefined" &&
      typeof ScrollTrigger !== "undefined" &&
      typeof Draggable !== "undefined" &&
      typeof Flip !== "undefined"
    ) {
      gsap.registerPlugin(ScrollTrigger, Draggable, Flip);

      let mm = gsap.matchMedia();

      mm.add({
          isDesktop: `(min-width: 1000px)`,
          isMobile: `(max-width: 999px)`,
          reduceMotion: "(prefers-reduced-motion: reduce)",
        },
        (context) => {
          let { isDesktop, isMobile, reduceMotion } = context.conditions;

          // HOMEPAGE CAN ANIMATION --------------------------------
          const can = document.querySelector(".hp-content__image-wrapper.main"),
            canStop = document.querySelector(".can-stop");

          if (can && canStop) {
            const canConfig = {
              trigger: can,
              endTrigger: canStop,
              start: "center-=40px center",
              end: "top-=60px bottom",
              scrub: !0,
              pin: can,
              pinSpacing: !1,
            };

            gsap.to(can, { scrollTrigger: canConfig });
          }

          // HERO ANIMATION --------------------------------
          const logo = document.querySelector("#homepage__logo");
          const hero = document.querySelector(".index-hero");

          if (logo && hero) {
            let mobileX = 0;

            if (isMobile) {
              const container = hero; // change to your actual container if needed
              const logoLeft = logo.getBoundingClientRect().left;
              const containerLeft = container.getBoundingClientRect().left;
              mobileX = 16 - (logoLeft - containerLeft);
            }

            ScrollTrigger.create({
              animation: gsap.from("#homepage__logo", {
                transformOrigin: "bottom left",
                y: isDesktop ? "50vh" : "25vh",
                scale: isDesktop ? 4 : 3,
                yPercent: -50,
                xPercent: isDesktop ? -70 : 0,
                x: isDesktop ? 0 : mobileX,
              }),
              scrub: true,
              trigger: ".index-hero",
              start: "top top",
              endTrigger: ".index-hero",
              end: "center top",
            });
          }
        }
      );

      mm.add("(min-width: 900px)", () => {
        // ABOUT STICKER ANIMATION --------------------------------
        const stickers = gsap.utils.toArray(".about-custom__image-container");
        if (!stickers.length) return;

        stickers.forEach((sticker) => {
          ScrollTrigger.create({
            trigger: sticker,
            endTrigger: ".about-custom__tab:last-child",
            start: "center center",
            end: "center center",
            pin: true,
            pinSpacing: false,
          });
        });
      });

      // DRAGGABLE ANIMATION --------------------------------
      if (
        document.querySelector(".draggable-sticker") &&
        document.querySelector(".draggable-wrapper")
      ) {
        Draggable.create(".draggable-sticker", {
          bounds: ".draggable-wrapper",
        });
      }

      const bgImage = document.querySelector(".draggable--bg-image");
      if (bgImage) {
        bgImage.ondragstart = function () {
          return false;
        };
      }

      // HERO SPLIT REVEAL --------------------------------
      document.querySelectorAll(".hero--split-reveal").forEach((section) => {
        const leftPanel = section.querySelector(".hero__split-panel--left");
        const rightPanel = section.querySelector(".hero__split-panel--right");
        const sticker = section.querySelector(".hero__sticker");

        if (!leftPanel || !rightPanel) return;

        gsap
          .timeline({
            scrollTrigger: {
              trigger: section,
              start: "top top",
              end: "+=100%",
              scrub: true,
              pin: true,
            },
          })
          .to(leftPanel, { xPercent: -100, ease: "none" }, 0)
          .to(rightPanel, { xPercent: 100, ease: "none" }, 0)
          .to(sticker, { scale: 0.9, opacity: 0, ease: "none" }, 0);
      });
    }

    // Non-GSAP init
    new Modal();
    externalLinkHandler.init();
    HScroll.init();
    AccordionDefaultOpenFix.init();
    BrickShare.init();
  });
  
  // Only run on pages with the store locator
  if (!document.getElementById('store-locator')) return;

  var savedY = window.scrollY;
  var active = true;

  function release() { active = false; }
  ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach(function (evt) {
    window.addEventListener(evt, release, { once: true, passive: true });
  });

  window.addEventListener('scroll', function () {
    if (active) window.scrollTo(0, savedY);
  });

  setTimeout(release, 6000);

  // ^^ Keep your scripts inside this IIFE function call to
  // avoid leaking your variables into the global scope.
})();